import { supabase } from '../../lib/supabase/client'
import { TABLES } from '../../lib/supabase/contracts.full'
import type { Plan, PlanDaySnapshot, PlanExerciseSnapshot } from '../plans/api'

export type WorkoutSession = {
  id: string
  athlete_id: string
  plan_id: string | null
  plan_version_id: string | null
  client_id: string
  name: string
  status: 'in_progress' | 'completed' | 'completed_early' | 'aborted' | 'skipped'
  started_at: string | null
  ended_at: string | null
  duration_seconds: number | null
  source_day_id: string | null
  source_day_index: number | null
  workout_snapshot: unknown
}

export type WorkoutSet = {
  id: string
  session_id: string
  athlete_id: string
  exercise_id: string | null
  client_id: string
  set_index: number
  set_type: string
  status: 'planned' | 'completed' | 'skipped'
  weight_kg: number | null
  reps: number | null
  duration_seconds: number | null
  distance_m: number | null
  rir: number | null
  rpe: number | null
  completed_at: string | null
  exercise_order: number
  exercise_name_snapshot: string | null
  tracking_type: string
  target_value: string | null
  rest_seconds: number
}

function clientId() { return crypto.randomUUID() }
function setTypeFor(exercise: PlanExerciseSnapshot, setIndex: number) { return exercise.setTypes?.[setIndex - 1] ?? exercise.setType ?? 'working' }

export async function findResumableWorkout(athleteId: string): Promise<WorkoutSession | null> {
  const { data, error } = await supabase.from(TABLES.workoutSessions).select('*').eq('athlete_id', athleteId).eq('status', 'in_progress').order('started_at', { ascending: false }).limit(1).maybeSingle()
  if (error) throw error
  return data as WorkoutSession | null
}

export async function startPlanWorkout(input: { athleteId: string; plan: Plan; day: PlanDaySnapshot; dayIndex: number }): Promise<WorkoutSession> {
  const existing = await findResumableWorkout(input.athleteId)
  if (existing) return existing
  const now = new Date().toISOString()
  const snapshot = { type: 'plan', planName: input.plan.name, name: input.day.name, day: input.day, exercises: input.day.exercises }
  const { data: session, error: sessionError } = await supabase.from(TABLES.workoutSessions).insert({ athlete_id: input.athleteId, plan_id: input.plan.id, plan_version_id: input.plan.current_version_id, client_id: clientId(), name: input.day.name, status: 'in_progress', started_at: now, source_day_id: input.day.id, source_day_index: input.dayIndex, workout_snapshot: snapshot }).select('*').single()
  if (sessionError) throw sessionError
  const rows = input.day.exercises.flatMap((exercise, exerciseIndex) => Array.from({ length: Math.max(1, Number(exercise.sets ?? 1)) }, (_, index) => ({ session_id: session.id, athlete_id: input.athleteId, exercise_id: exercise.exerciseId ?? null, client_id: clientId(), set_index: index + 1, set_type: setTypeFor(exercise, index + 1), status: 'planned', weight_kg: exercise.targetWeightKg ?? null, reps: null, exercise_order: exerciseIndex + 1, exercise_name_snapshot: exercise.name, tracking_type: exercise.trackingType ?? 'weight_reps', target_value: exercise.reps == null ? null : String(exercise.reps), rest_seconds: Math.max(0, Number(exercise.restSeconds ?? 0)) })))
  if (rows.length) {
    const { error: setsError } = await supabase.from(TABLES.workoutSets).insert(rows)
    if (setsError) { await supabase.from(TABLES.workoutSessions).update({ status: 'aborted', ended_at: new Date().toISOString() }).eq('id', session.id); throw setsError }
  }
  return session as WorkoutSession
}

export async function loadWorkout(sessionId: string) {
  const [sessionResult, setsResult] = await Promise.all([supabase.from(TABLES.workoutSessions).select('*').eq('id', sessionId).single(), supabase.from(TABLES.workoutSets).select('*').eq('session_id', sessionId).order('exercise_order').order('set_index')])
  if (sessionResult.error) throw sessionResult.error
  if (setsResult.error) throw setsResult.error
  return { session: sessionResult.data as WorkoutSession, sets: (setsResult.data ?? []) as WorkoutSet[] }
}

export async function completeSet(setId: string, values: { weightKg?: number | null; reps?: number | null; durationSeconds?: number | null; distanceM?: number | null; rir?: number | null; rpe?: number | null }) {
  const { data, error } = await supabase.from(TABLES.workoutSets).update({ status: 'completed', weight_kg: values.weightKg ?? null, reps: values.reps ?? null, duration_seconds: values.durationSeconds ?? null, distance_m: values.distanceM ?? null, rir: values.rir ?? null, rpe: values.rpe ?? null, completed_at: new Date().toISOString() }).eq('id', setId).select('*').single()
  if (error) throw error
  return data as WorkoutSet
}

export async function completeWorkout(session: WorkoutSession) {
  const endedAt = new Date(); const startedAt = session.started_at ? new Date(session.started_at) : endedAt
  const durationSeconds = Math.max(0, Math.round((endedAt.getTime() - startedAt.getTime()) / 1000))
  const { data, error } = await supabase.from(TABLES.workoutSessions).update({ status: 'completed', ended_at: endedAt.toISOString(), duration_seconds: durationSeconds }).eq('id', session.id).select('*').single()
  if (error) throw error
  return data as WorkoutSession
}
